import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
  InputAdornment,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import {
  Search as SearchIcon,
  Psychology as SemanticIcon,
  TextFields as TextIcon,
  Article as DocumentIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { documentApi, knowledgeEntryApi, userApi, Document, KnowledgeEntry, User, Department } from '../services/api';
import DepartmentFilter from '../components/DepartmentFilter';
import PageHeader from '../components/PageHeader';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`search-tabpanel-${index}`}
      aria-labelledby={`search-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const Search: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState<number>(0);
  const [textQuery, setTextQuery] = useState<string>('');
  const [semanticQuery, setSemanticQuery] = useState<string>('');
  const [textResults, setTextResults] = useState<KnowledgeEntry[]>([]);
  const [semanticResults, setSemanticResults] = useState<KnowledgeEntry[]>([]);
  const [documentResults, setDocumentResults] = useState<Document[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDepartments, setSelectedDepartments] = useState<number[]>([]);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userDepartments, setUserDepartments] = useState<Department[]>([]);

  // 加载当前用户信息
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const user = await userApi.getCurrentUser();
        setCurrentUser(user);
        setUserDepartments(user.departments || []);
      } catch (err) {
        console.error('获取当前用户信息失败:', err);
      }
    };

    loadCurrentUser();
  }, []);

  const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
    setTabValue(newValue);
  };

  const handleDepartmentChange = (departmentIds: number[]) => {
    setSelectedDepartments(departmentIds);
  };

  const highlightSearchTerm = (text: string, searchTerm: string): React.ReactNode => {
    if (!searchTerm.trim()) return text;
    
    const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
    return (
      <>
        {parts.map((part, index) => 
          part.toLowerCase() === searchTerm.toLowerCase() ? 
            <span key={index} className="search-highlight">{part}</span> : part
        )}
      </>
    );
  };

  const handleTextSearch = async () => {
    if (!textQuery.trim()) return;

    try {
      setLoading(true);
      setError(null);
      
      console.log('文本搜索 - 查询:', textQuery, '部门IDs:', selectedDepartments);
      const results = await knowledgeEntryApi.searchByContent(textQuery, selectedDepartments);
      console.log('文本搜索结果:', results);
      setTextResults(results);
      setLoading(false);
    } catch (err) {
      console.error('文本搜索失败:', err);
      setError('搜索失败，请重试');
      setLoading(false);
    }
  };

  const handleSemanticSearch = async () => {
    if (!semanticQuery.trim()) return;

    try {
      setLoading(true);
      setError(null);
      
      const results = await knowledgeEntryApi.searchSemantic(semanticQuery, 10, selectedDepartments);
      setSemanticResults(results);
      setLoading(false);
    } catch (err: any) {
      console.error('语义搜索失败:', err);
      let errorMessage = '搜索失败，请重试';
      if (err.response && err.response.data) {
        errorMessage = `搜索失败: ${err.response.data}`;
      } else if (err.message) {
        errorMessage = `搜索失败: ${err.message}`;
      }
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleDocumentSearch = async () => {
    if (!textQuery.trim()) return;

    try {
      setLoading(true);
      setError(null);
      
      const results = await documentApi.searchDocuments(textQuery);
      setDocumentResults(results);
      setLoading(false);
    } catch (err) {
      console.error('文档搜索失败:', err);
      setError('搜索失败，请重试');
      setLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent, searchType: string) => {
    if (event.key === 'Enter') {
      if (searchType === 'text') {
        handleTextSearch();
      } else if (searchType === 'semantic') {
        handleSemanticSearch();
      } else if (searchType === 'document') {
        handleDocumentSearch();
      }
    }
  };

  return (
    <Box className="page-container">
      <PageHeader
        title="知识搜索"
        subtitle="关键词与语义检索，快速定位企业知识片段"
        breadcrumbs={[{ label: '知识库', to: '/documents' }, { label: '知识搜索' }]}
      />

      {/* 部门筛选 */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          部门筛选
          {selectedDepartments.length > 0 && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              (已选择 {selectedDepartments.length} 个部门)
            </Typography>
          )}
        </Typography>
        <DepartmentFilter
          selectedDepartments={selectedDepartments}
          onDepartmentChange={handleDepartmentChange}
          userDepartments={userDepartments}
        />
      </Paper>

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="文本搜索" icon={<TextIcon />} />
          <Tab label="语义搜索" icon={<SemanticIcon />} />
          <Tab label="文档搜索" icon={<DocumentIcon />} />
        </Tabs>

        {/* 文本搜索面板 */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="输入关键词进行精确文本搜索..."
                value={textQuery}
                onChange={(e) => setTextQuery(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, 'text')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                onClick={handleTextSearch}
                disabled={!textQuery.trim() || loading}
              >
                文本搜索
              </Button>
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              搜索结果 ({textResults.length})
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : textResults.length > 0 ? (
              <Grid container spacing={3}>
                {textResults.map((entry) => (
                  <Grid item xs={12} key={entry.id}>
                    <Card>
                      <CardContent>
                        <Typography variant="body1" gutterBottom>
                          {highlightSearchTerm(entry.content, textQuery)}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Chip 
                            label={`来自: ${entry.document.title}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      </CardContent>
                      <CardActions>
                        <Button 
                          size="small" 
                          startIcon={<ViewIcon />}
                          onClick={() => navigate(`/documents/${entry.document.id}`)}
                        >
                          查看文档
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : textQuery ? (
              <Typography variant="body1" color="text.secondary" align="center">
                没有找到匹配的结果
              </Typography>
            ) : (
              <Typography variant="body1" color="text.secondary" align="center">
                请输入搜索关键词
              </Typography>
            )}
          </Box>
        </TabPanel>

        {/* 语义搜索面板 */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="用自然语言描述你想找的内容..."
                value={semanticQuery}
                onChange={(e) => setSemanticQuery(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, 'semantic')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SemanticIcon />
                    </InputAdornment>
                  ),
                }}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="secondary"
                fullWidth
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SemanticIcon />}
                onClick={handleSemanticSearch}
                disabled={!semanticQuery.trim() || loading}
              >
                语义搜索
              </Button>
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              搜索结果 ({semanticResults.length})
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : semanticResults.length > 0 ? (
              <Grid container spacing={3}>
                {semanticResults.map((entry) => (
                  <Grid item xs={12} key={entry.id}>
                    <Card>
                      <CardContent>
                        <Typography variant="body1" gutterBottom>
                          {entry.content}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Chip 
                            label={`来自: ${entry.document.title}`}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        </Box>
                      </CardContent>
                      <CardActions>
                        <Button 
                          size="small" 
                          startIcon={<ViewIcon />}
                          onClick={() => navigate(`/documents/${entry.document.id}`)}
                        >
                          查看文档
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : semanticQuery ? (
              <Typography variant="body1" color="text.secondary" align="center">
                没有找到匹配的结果
              </Typography>
            ) : (
              <Typography variant="body1" color="text.secondary" align="center">
                请输入搜索问题或描述
              </Typography>
            )}
          </Box>
        </TabPanel>

        {/* 文档搜索面板 */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="搜索文档标题..."
                value={textQuery}
                onChange={(e) => setTextQuery(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, 'document')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DocumentIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <DocumentIcon />}
                onClick={handleDocumentSearch}
                disabled={!textQuery.trim() || loading}
              >
                搜索文档
              </Button>
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              搜索结果 ({documentResults.length})
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : documentResults.length > 0 ? (
              <Grid container spacing={3}>
                {documentResults.map((document) => (
                  <Grid item xs={12} sm={6} md={4} key={document.id}>
                    <Card className="document-card card-hover">
                      <CardContent className="document-card-content">
                        <Typography variant="h6" gutterBottom>
                          {highlightSearchTerm(document.title, textQuery)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {document.description || '无描述'}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Chip 
                            label={document.fileType} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                          {document.category && (
                            <Chip 
                              label={document.category} 
                              size="small" 
                              sx={{ ml: 1 }}
                              color="secondary" 
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </CardContent>
                      <CardActions>
                        <Button 
                          size="small" 
                          startIcon={<ViewIcon />}
                          onClick={() => navigate(`/documents/${document.id}`)}
                        >
                          查看文档
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : textQuery ? (
              <Typography variant="body1" color="text.secondary" align="center">
                没有找到匹配的文档
              </Typography>
            ) : (
              <Typography variant="body1" color="text.secondary" align="center">
                请输入搜索关键词
              </Typography>
            )}
          </Box>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default Search; 